import React, { useMemo } from "react"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { Box, IconButton, Tooltip } from "@mui/material"
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import AddIcon from "@mui/icons-material/Add"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DataGridTable from "src/v2/components/Table/data-grid-table"
import ButtonWithIcon from "src/v2/components/Button/Button"
import theme from "src/v2/theme/theme"
import { formatCpfCnpj } from "src/v2/utils/formatter/format-cpf-cnpj"
import { FIELD_TYPE } from "./company-registration-config"

const formatDate = (value) => {
    if (!value) return "-"
    const date = dayjs(value)
    return date.isValid() ? date.format("DD/MM/YYYY") : value
}

const CompanyRegistrationTable = ({ definition, table, onAdd, onView, onEdit, onDelete }) => {
    const showPagination = definition.showPaginationWhenMultiple && table.rowCount > table.pageSize
    const allowView = definition.allowView === true
    const allowEdit = definition.allowEdit !== false
    const allowDelete = definition.allowDelete !== false
    const actionCount = Number(allowView) + Number(allowEdit) + Number(allowDelete)
    const columns = useMemo(() => {
        const listColumns = definition.listColumns.map((column) => ({
            field: column.field,
            headerName: column.headerName,
            minWidth: column.field === "RazaoSocial" ? 220 : 150,
            flex: column.field === "RazaoSocial" ? 1.4 : 1,
            valueFormatter: column.type === FIELD_TYPE.DATE
                ? ({ value }) => formatDate(value)
                : column.field === "CPFCNPJ"
                    ? ({ value }) => formatCpfCnpj(value)
                    : undefined,
        }))

        if (actionCount === 0) return listColumns

        return [...listColumns, {
            field: "acoes",
            headerName: "Ações",
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            width: actionCount * 58,
            renderCell: ({ row }) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {allowView && (
                        <Tooltip title="Visualizar registro">
                            <IconButton aria-label="Visualizar registro" onClick={() => onView(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                                <VisibilityIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {allowEdit && (
                        <Tooltip title="Editar registro">
                            <IconButton aria-label="Editar registro" onClick={() => onEdit(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                                <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {allowDelete && (
                        <Tooltip title="Excluir registro">
                            <IconButton aria-label="Excluir registro" onClick={() => onDelete(row)} sx={{ color: theme.palette.icon.main, "&:focus-visible": { outline: `2px solid ${theme.palette.common.white}`, outlineOffset: 2 } }}>
                                <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        }]
    }, [actionCount, allowDelete, allowEdit, allowView, definition.listColumns, onDelete, onEdit, onView])

    return (
        <Box sx={{ width: "100%", minWidth: 0 }}>
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1.5, mb: definition.allowCreate ? 2 : 0 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {definition.allowCreate && <ButtonWithIcon icon={<AddIcon />} onClick={onAdd}>ADICIONAR NOVO</ButtonWithIcon>}
                </Box>
            </Box>

            <Box sx={{ height: "clamp(320px, 52dvh, 560px)", minHeight: 0 }}>
                <DataGridTable
                    contained
                    serverSide
                    showPagination={showPagination}
                    rows={table.rows}
                    columns={columns}
                    loading={table.loading}
                    rowCount={table.rowCount}
                    page={table.page}
                    pageSize={table.pageSize}
                    onServerPageChange={(nextPage, nextPageSize) => table.loadPage({ nextPage, nextPageSize })}
                    getRowId={(row) => row.ID}
                />
            </Box>
        </Box>
    )
}

CompanyRegistrationTable.propTypes = {
    definition: PropTypes.object.isRequired,
    table: PropTypes.shape({
        rows: PropTypes.array.isRequired,
        rowCount: PropTypes.number.isRequired,
        page: PropTypes.number.isRequired,
        pageSize: PropTypes.number.isRequired,
        loading: PropTypes.bool.isRequired,
        loadPage: PropTypes.func.isRequired,
    }).isRequired,
    onAdd: PropTypes.func.isRequired,
    onView: PropTypes.func,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
}

CompanyRegistrationTable.defaultProps = {
    onView: undefined,
}

export default CompanyRegistrationTable
